import type { LessonItem, UnitItem } from "@/components/course/StudyList";
import type {
  CourseBuilderFormValues,
  CourseBuilderModel,
  CourseBuilderTerm,
  CourseBuilderUnit,
  CourseModel,
  CourseModelList,
  CourseTerm,
  CreateCoursePayload,
  CourseLesson,
  CourseUnit,
  UpdateCoursePayload,
  UpdateCourseUnitPayload,
} from "@/types/course.model";

const defaultUnitNotify = { assignment: 0, query: 0 };

const defaultCourseNotify = {
  assignment: {
    general_assignment: "0",
    unit_assignment: "0",
    total: "0",
  },
  query: 0,
};

function normalizeUnit(unit: Partial<CourseUnit>): CourseUnit {
  const uploadId = unit.s3_upload_id?.trim() || unit.upload?.id?.trim() || "";
  const s3Path = unit.s3_path ?? unit.upload?.s3_path;

  return {
    id: unit.id as string,
    name: unit.name as string,
    is_active: unit.is_active ?? true,
    status: unit.status ?? "PENDING",
    notify: unit.notify ?? defaultUnitNotify,
    is_completed: unit.is_completed ?? false,
    duration: unit.duration,
    upload: unit.upload,
    s3_upload_id: uploadId || undefined,
    s3_path: s3Path,
  };
}

function normalizeLesson(lesson: Partial<CourseLesson>): CourseLesson {
  return {
    id: lesson.id as string,
    name: lesson.name as string,
    is_active: lesson.is_active ?? true,
    status: lesson.status ?? "PENDING",
    units: (lesson.units ?? []).map((u) => normalizeUnit(u)),
  };
}

function normalizeTerm(term: Partial<CourseTerm>): CourseTerm {
  return {
    id: term.id as string,
    name: term.name as string,
    is_active: term.is_active ?? true,
    status: term.status ?? "PENDING",
    lessons: (term.lessons ?? []).map((l) => normalizeLesson(l)),
  };
}

/** Normalize view API response (nullable notify, terms[], etc.) */
export function normalizeCourseView(data: CourseModel): CourseModel {
  const terms = data.terms?.map((t) => normalizeTerm(t));
  const lessons =
    data.lessons?.map((l) => normalizeLesson(l)) ??
    terms?.flatMap((t) => t.lessons) ??
    [];

  return {
    ...data,
    course_completion: data.course_completion ?? "0",
    notify: data.notify ?? defaultCourseNotify,
    terms,
    lessons,
  };
}

type ViewCourseUnit = CourseUnit & {
  file_name?: string;
  uploaded_file_name?: string;
  file_url?: string;
};

function mapViewUnitToBuilder(
  unit: ViewCourseUnit,
  gradeName: string,
  lessonName: string
): CourseBuilderUnit {
  const s3UploadId =
    unit.s3_upload_id?.trim() || unit.upload?.id?.trim() || "";
  const s3Path = unit.s3_path ?? unit.upload?.s3_path;
  const hasUpload = Boolean(s3UploadId);
  const displayName =
    unit.uploaded_file_name ??
    unit.s3_path ??
    unit.upload?.s3_path ??
    unit.file_name;
  const legacyFileName = unit.file_name ?? (hasUpload ? undefined : unit.name);

  return {
    id: unit.id,
    name: unit.name,
    s3_upload_id: s3UploadId,
    s3_path: s3Path,
    uploaded_file_name: displayName ?? legacyFileName,
    file_name: legacyFileName,
    file_url: unit.file_url,
    upload_course_name: hasUpload ? gradeName : "",
    upload_lesson_name: hasUpload ? lessonName : "",
    upload_unit_name: hasUpload ? unit.name : "",
  };
}

function sortByOrder<T extends { order?: number }>(items: T[]): T[] {
  if (!items.some((item) => item.order != null)) return items;
  return [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

function mapViewLessonToBuilder(
  lesson: CourseLesson,
  gradeName: string
): CourseBuilderTerm["lessons"][number] {
  return {
    id: lesson.id,
    name: lesson.name,
    units: sortByOrder(lesson.units).map((u) =>
      mapViewUnitToBuilder(u as ViewCourseUnit, gradeName, lesson.name)
    ),
  };
}

/** Map GET /cs/api/v1/view/:id response into builder form model for edit */
export function courseViewToBuilderModel(course: CourseModel): CourseBuilderModel {
  const grade_name = course.name;

  let terms: CourseBuilderTerm[];

  if (course.terms?.length) {
    terms = course.terms.map((term) => ({
      id: term.id,
      name: term.name,
      lessons: sortByOrder(term.lessons).map((l) =>
        mapViewLessonToBuilder(l, grade_name)
      ),
    }));
  } else if (course.lessons?.length) {
    terms = [
      {
        id: `term-${course.id}-0`,
        name: "Term 1",
        lessons: sortByOrder(course.lessons).map((l) =>
          mapViewLessonToBuilder(l, grade_name)
        ),
      },
    ];
  } else {
    terms = [
      {
        id: `term-${course.id}-0`,
        name: "Term 1",
        lessons: [
          {
            id: `lesson-${course.id}-0`,
            name: "Lesson 1",
            units: [],
          },
        ],
      },
    ];
  }

  return {
    id: course.id,
    grade_name,
    is_published: course.is_published,
    publish_status:
      course.is_published === false
        ? "DRAFT"
        : course.is_published === true
          ? "PUBLISHED"
          : "PUBLISHED",
    is_active: course.is_active,
    terms,
  };
}

/** Merge router state, list row, and view API for edit publish/active flags */
export function resolveCoursePublishMeta(
  listState: { is_published?: boolean; is_active?: boolean },
  listRow: { is_published?: boolean; is_active: boolean } | null,
  view: { is_published?: boolean; is_active: boolean }
): { is_published?: boolean; is_active: boolean } {
  return {
    is_published:
      listState.is_published ?? listRow?.is_published ?? view.is_published,
    is_active: listState.is_active ?? listRow?.is_active ?? view.is_active,
  };
}

export function needsListPublishMetaLookup(
  listState: { is_published?: boolean },
  view: { is_published?: boolean }
): boolean {
  return (
    listState.is_published === undefined && view.is_published === undefined
  );
}

export function mergeBuilderPublishState(
  model: CourseBuilderModel,
  overrides?: { is_published?: boolean; is_active?: boolean }
): CourseBuilderModel {
  const explicit = overrides?.is_published ?? model.is_published;
  /** Unknown → published to avoid accidental unpublish via Save as Draft */
  const isPublished =
    explicit === false ? false : explicit === true ? true : true;
  return {
    ...model,
    is_active: overrides?.is_active ?? model.is_active,
    is_published: isPublished,
    publish_status: isPublished ? "PUBLISHED" : "DRAFT",
  };
}

export function isBuilderCoursePublished(model?: CourseBuilderModel): boolean {
  if (!model) return true;
  if (model.is_published === false) return false;
  if (model.is_published === true) return true;
  return model.publish_status !== "DRAFT";
}

/** Course list status pill: draft when active but not published */
export function getCourseListDisplayStatus(
  record: Pick<CourseModelList, "is_active" | "is_published" | "publish_status">
): {
  label: string;
  variant: "draft" | "active" | "inactive";
  isDraft: boolean;
} {
  /** Prefer API `is_published`; legacy `publish_status` only when `is_published` omitted */
  const isUnpublished =
    record.is_published === false ||
    (record.is_published === undefined && record.publish_status === "DRAFT");
  const isDraft = record.is_active === true && isUnpublished;

  if (isDraft) {
    return { label: "Save as draft", variant: "draft", isDraft: true };
  }
  if (record.is_active) {
    return { label: "Active", variant: "active", isDraft: false };
  }
  return { label: "Inactive", variant: "inactive", isDraft: false };
}

function mapBuilderTermsToCreatePayload(
  values: CourseBuilderFormValues
): CreateCoursePayload["terms"] {
  return values.terms.map((term) => ({
    name: term.name.trim(),
    lessons: term.lessons.map((lesson, lessonIndex) => ({
      name: lesson.name.trim(),
      order: lessonIndex + 1,
      units: lesson.units.map((unit, unitIndex) => ({
        name: unit.name.trim(),
        s3_upload_id: (unit.s3_upload_id ?? "").trim(),
        order: unitIndex + 1,
      })),
    })),
  }));
}

function mapBuilderTermsToUpdatePayload(
  values: CourseBuilderFormValues
): UpdateCoursePayload["terms"] {
  return values.terms.map((term) => ({
    name: term.name.trim(),
    lessons: term.lessons.map((lesson, lessonIndex) => ({
      name: lesson.name.trim(),
      order: lessonIndex + 1,
      units: lesson.units.map((unit, unitIndex) => ({
        name: unit.name.trim(),
        s3_upload_id: (unit.s3_upload_id ?? "").trim(),
        order: unitIndex + 1,
      })),
    })),
  }));
}

export function toCreateCoursePayload(
  values: CourseBuilderFormValues,
  options: { isPublished: boolean }
): CreateCoursePayload {
  return {
    grade: values.grade_name.trim(),
    is_published: options.isPublished,
    terms: mapBuilderTermsToCreatePayload(values),
  };
}

export function toUpdateCoursePayload(
  values: CourseBuilderFormValues,
  options: { isPublished: boolean; isActive: boolean }
): UpdateCoursePayload {
  return {
    grade: values.grade_name.trim(),
    is_published: options.isPublished,
    is_active: options.isActive,
    terms: mapBuilderTermsToUpdatePayload(values),
  };
}

function mapUnitToStudyList(unit: CourseUnit): UnitItem {
  return {
    id: unit.id,
    name: unit.name,
    status: unit.status ?? undefined,
    is_active: unit.is_active,
    notify: unit.notify ?? defaultUnitNotify,
    is_completed: unit.is_completed,
    duration: unit.duration,
  };
}

function mapLessonToStudyList(lesson: CourseLesson): LessonItem {
  return {
    id: lesson.id,
    name: lesson.name,
    units: lesson.units.map(mapUnitToStudyList),
    isExpanded: true,
  };
}

export function toStudyListTerms(
  course: CourseModel
): { name: string; lessons: LessonItem[] }[] {
  if (course.terms?.length) {
    return course.terms.map((term) => ({
      name: term.name,
      lessons: term.lessons.map(mapLessonToStudyList),
    }));
  }

  if (course.lessons?.length) {
    return [{ name: "Term 1", lessons: course.lessons.map(mapLessonToStudyList) }];
  }

  return [];
}

export function getFirstLessonUnit(
  course: CourseModel
): { lesson: CourseLesson; unit: CourseUnit } | null {
  const terms = course.terms;
  if (terms?.length) {
    const lesson = terms[0]?.lessons?.[0];
    const unit = lesson?.units?.[0];
    if (lesson && unit) return { lesson, unit };
  }

  const lesson = course.lessons?.[0];
  const unit = lesson?.units?.[0];
  if (lesson && unit) return { lesson, unit };

  return null;
}

export function findLessonInCourse(
  course: CourseModel,
  lessonId: string
): CourseLesson | undefined {
  for (const term of course.terms ?? []) {
    const found = term.lessons.find((l) => l.id === lessonId);
    if (found) return found;
  }
  return course.lessons?.find((l) => l.id === lessonId);
}

/** Lookup units by id (term-aware; avoids wrong index when switching term tabs) */
export function buildCourseUnitLookup(course: CourseModel): Map<string, CourseUnit> {
  const map = new Map<string, CourseUnit>();

  const addLessons = (lessons: CourseLesson[]) => {
    for (const lesson of lessons) {
      for (const unit of lesson.units) {
        map.set(unit.id, unit);
      }
    }
  };

  if (course.terms?.length) {
    for (const term of course.terms) {
      addLessons(term.lessons);
    }
  } else if (course.lessons?.length) {
    addLessons(course.lessons);
  }

  return map;
}
